type InvitationPrefillSource = {
  isLinkInvite?: boolean | null;
  invitationEmail?: string | null;
};

export type InvitationSignupPrefill = {
  /**
   * Request-to-join mode: drives the "Request to Join" CTA, the `entity_id`
   * register payload and the post-signup request flow in `ScopedSignupModal`.
   */
  isLinkInvite: boolean;
  /** Prefill and lock the email field to `email`. */
  isEmailLocked: boolean;
  email: string;
};

/**
 * What the scoped signup form should start with for a given invitation.
 *
 * Two decisions that must NOT be derived from one flag:
 *
 * - **Request mode** (`isLinkInvite`) — a link invite, or any invitation that
 *   has expired, can no longer be accepted directly, so the form stays in
 *   request-to-join mode ("Request to Join" CTA).
 * - **Email lock** (`isEmailLocked`) — decided only by the invitation shape.
 *   TA-10943: an invitation does not change shape when it expires. An expired
 *   email invitation still names an address, and the backend matches the
 *   resulting join request to that invitation by the registering user's email
 *   (`CreateUserRequest` → `with_normalized_email(user.email)`), so the field is
 *   prefilled and read-only.
 *
 * The first TA-10943 fix returned only `isLinkInvite`, so locking the email
 * also flipped the CTA to "Join This Project/Task" — the QA-failed regression.
 *
 * Absent/unknown `isLinkInvite` falls back to the link-invite shape — an empty,
 * editable box. That is the safe direction: it asks the user for an address
 * rather than locking them into one we are not sure about.
 */
export const resolveInvitationSignupPrefill = (
  invitation?: InvitationPrefillSource | null,
  { isExpired = false }: { isExpired?: boolean } = {}
): InvitationSignupPrefill => {
  const isLinkShape = invitation?.isLinkInvite ?? true;
  return {
    isLinkInvite: isLinkShape || isExpired,
    isEmailLocked: !isLinkShape,
    email: isLinkShape ? '' : (invitation?.invitationEmail ?? ''),
  };
};
